import {
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious'
import RepeatIcon from '@mui/icons-material/Repeat'
import RepeatOneIcon from '@mui/icons-material/RepeatOne'
import AllInclusiveIcon from '@mui/icons-material/AllInclusive'
import { useTranslation } from 'react-i18next'
import { getLocalizedSurahName, getSurahInfo } from '../lib/surahs'
import { useSurahTiming } from '../hooks/useSurahTiming'
import type { SurahRepeatPlaythrough } from '../hooks/usePlaylistPlayer'
import { VolumeControl } from './VolumeControl'

type RepeatMode = 'off' | 'repeat-all' | 'repeat-one'

interface InlinePlayerProps {
  isPlaying: boolean
  loading?: boolean
  currentTime: number
  duration: number
  /** Number of the currently-playing surah. Drives the label + the
   *  total-ayah count for the progress text. Null when nothing is loaded. */
  activeSurahNumber: number | null
  /** 1-based ayah index within `activeSurahNumber`. Null during preamble. */
  currentAyahNumber: number | null
  onPlayPause: () => void
  onNext: () => void
  onPrevious: () => void
  /** Fine-grained seek WITHIN the current ayah (engine-level). Kept on
   *  the interface for future controls (e.g., a +/-10s skip), but the
   *  progress bar uses `onSeekToAyah` instead — it's a surah-wide bar. */
  onSeek?: (time: number) => void
  /** Optional: jump to the ayah at this 0-based index inside the active
   *  surah. When provided, the progress bar becomes click-to-seek across
   *  the WHOLE SURAH (each click maps the click position to the target
   *  ayah). When omitted, the bar is read-only (still shows surah-wide
   *  progress visually). */
  onSeekToAyah?: (ayahIndex: number) => void
  /** Optional repeat-mode cycler — playlist context. When omitted the
   *  button is hidden, keeping the bar slim for callers that don't have
   *  playlist semantics (the reader). */
  repeatMode?: RepeatMode
  onCycleRepeat?: () => void
  /** Optional continuous-play toggle — reader context. When the reader is
   *  in continuous mode, audio auto-advances ayah-by-ayah; in single-ayah
   *  mode it stops after each. Hidden when omitted. */
  continuousMode?: boolean
  onToggleContinuousMode?: () => void
  /** Current volume in [0, 1]. When both this and `onVolumeChange` are given,
   *  a volume control is shown (sm+ — mobile relies on hardware volume). */
  volume?: number
  onVolumeChange?: (value: number) => void
  /** Outer surah-repeat playthrough (e.g. 3/10). Hidden when omitted or null. */
  surahRepeatPlaythrough?: SurahRepeatPlaythrough | null
}

const CLUSTER_BTN_SX = {
  width: 32,
  height: 32,
  flexShrink: 0,
  color: 'text.secondary',
} as const

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

/** Pad with figure space (U+2007) so digit count can grow without shifting layout. */
function padToWidth(text: string, width: number): string {
  return text.padStart(width, '\u2007')
}

/**
 * Slim fixed-bottom playback bar. Used by both the playlist detail page
 * (with repeat-mode) and the Quran reader (with continuous-mode). Single
 * row of controls + a 3px progress bar across the top. Mobile sits above
 * the BottomNav; desktop sits at the viewport bottom past the icon rail.
 */
export function InlinePlayer({
  isPlaying,
  currentTime,
  duration,
  activeSurahNumber,
  currentAyahNumber,
  onPlayPause,
  onNext,
  onPrevious,
  onSeekToAyah,
  repeatMode,
  onCycleRepeat,
  continuousMode,
  onToggleContinuousMode,
  volume,
  onVolumeChange,
  surahRepeatPlaythrough,
}: InlinePlayerProps) {
  const { t, i18n } = useTranslation()
  const theme = useTheme()

  const surahInfo = activeSurahNumber !== null ? getSurahInfo(activeSurahNumber) : null
  const totalAyahs = surahInfo?.numberOfAyahs ?? null
  // Real whole-surah elapsed/total time from the precomputed duration assets,
  // so the time readout isn't just the current ayah's length.
  const surahTiming = useSurahTiming(activeSurahNumber, currentAyahNumber, currentTime)
  const localizedName = activeSurahNumber !== null
    ? getLocalizedSurahName(activeSurahNumber, i18n.language)
    : null
  const transliteration = surahInfo?.name ?? null

  const repeatIcon =
    repeatMode === 'repeat-one' ? <RepeatOneIcon fontSize="small" /> : <RepeatIcon fontSize="small" />
  const repeatLabel =
    repeatMode === 'off'
      ? t('audio.repeatOff')
      : repeatMode === 'repeat-all'
        ? t('audio.repeatAll')
        : t('audio.repeatOne')
  const continuousLabel = continuousMode
    ? t('audio.continuousModeOn')
    : t('audio.continuousModeOff')

  // Compact "Al-Fatiha · 3/7" label — picks the most useful fields and
  // truncates with ellipsis to keep the single row short.
  const surahLabel = transliteration && localizedName
    ? `${transliteration} · ${localizedName}`
    : transliteration ?? localizedName ?? t('playlists.playlist')

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: {
          xs: 'calc(56px + var(--safe-bottom, 0px))',
          md: 0,
        },
        left: { xs: 0, md: '72px' },
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar - 1,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? '0 -4px 12px rgba(0,0,0,0.4)'
            : '0 -4px 10px rgba(15,118,110,0.08)',
        pb: {
          xs: 0,
          md: 'var(--safe-bottom, 0px)',
        },
        // Bar spans full width (left/right above stay 0); inset the content
        // via padding so it clears a landscape notch/rounded corner while
        // the background stays edge-to-edge. 0px on non-notched devices.
        pl: 'var(--safe-left, 0px)',
        pr: 'var(--safe-right, 0px)',
      }}
    >
      {/* 3px progress bar showing SURAH-WIDE progress (not just the current
          ayah). Computed as:
              ((currentAyah - 1) + currentTime / duration) / totalAyahs
          which treats each ayah as an equal slice of the surah. Close enough
          for UX without summing per-ayah durations we don't have upfront.
          When `onSeekToAyah` is wired, clicking the bar jumps to the ayah
          under the click. Otherwise the bar is purely visual. */}
      {(() => {
        const inAyah = duration > 0 ? Math.min(1, currentTime / duration) : 0
        const surahPct =
          totalAyahs && currentAyahNumber !== null
            ? Math.min(
                100,
                ((Math.max(0, currentAyahNumber - 1) + inAyah) / totalAyahs) * 100,
              )
            : 0
        const seekable = Boolean(onSeekToAyah && totalAyahs)
        return (
          <Box
            role="slider"
            aria-label={t('audio.seek')}
            aria-valuemin={0}
            aria-valuemax={totalAyahs ?? 0}
            aria-valuenow={currentAyahNumber ?? 0}
            onClick={(e) => {
              if (!seekable || !onSeekToAyah || !totalAyahs) return
              const rect = e.currentTarget.getBoundingClientRect()
              const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
              const targetIdx = Math.min(
                totalAyahs - 1,
                Math.max(0, Math.floor(pct * totalAyahs)),
              )
              onSeekToAyah(targetIdx)
            }}
            sx={{
              position: 'relative',
              height: 4,
              width: '100%',
              bgcolor: 'divider',
              cursor: seekable ? 'pointer' : 'default',
              touchAction: 'manipulation',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: `${surahPct}%`,
                bgcolor: 'primary.main',
                // Rounded leading edge + a soft glow for a more modern
                // "filled track" feel than a flat hairline.
                borderRadius: '0 2px 2px 0',
                boxShadow: (theme) => `0 0 6px ${alpha(theme.palette.primary.main, 0.5)}`,
                transition: 'width 0.15s linear',
              }}
            />
          </Box>
        )
      })()}

      {/* Single row of controls. Padding kept tight — total bar height
          ends up around 56-64px on mobile, even less than the BottomNav. */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          px: { xs: 1, sm: 1.5 },
          py: 0.5,
          minHeight: 56,
        }}
      >
        {/* Play / pause — compact (40px) instead of the big 60px circle. */}
        <Tooltip title={isPlaying ? t('audio.pause') : t('audio.play')}>
          <span>
            <IconButton
              onClick={onPlayPause}
              aria-label={isPlaying ? t('audio.pause') : t('audio.play')}
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                flexShrink: 0,
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          </span>
        </Tooltip>

        {/* Surah label + ayah X/Y — truncates if narrow. Stacks on a single
            line on xs to keep the bar slim; the ayah counter is a tabular-
            num sub-line on a separate row would have doubled the bar height
            (which is exactly what the user asked us NOT to do). */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 700, lineHeight: 1.2 }}
            title={surahLabel}
          >
            {surahLabel}
          </Typography>
          {currentAyahNumber !== null && totalAyahs !== null && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{
                fontVariantNumeric: 'tabular-nums',
                fontFeatureSettings: '"tnum" 1',
                lineHeight: 1.2,
              }}
            >
              {t('playlists.ayahProgress', {
                current: padToWidth(
                  String(currentAyahNumber),
                  String(totalAyahs).length,
                ),
                total: totalAyahs,
              })}
              {' · '}
              {surahTiming ? (
                <>
                  {padToWidth(
                    formatTime(surahTiming.elapsed),
                    formatTime(surahTiming.total).length,
                  )}
                  {' / '}
                  {formatTime(surahTiming.total)}
                </>
              ) : (
                formatTime(duration)
              )}
            </Typography>
          )}
        </Box>

        <Stack
          direction="row"
          spacing={0.5}
          sx={{ alignItems: 'center', flexShrink: 0 }}
        >
          <Tooltip title={t('audio.previous')}>
            <IconButton
              onClick={onPrevious}
              aria-label={t('audio.previous')}
              size="small"
              sx={CLUSTER_BTN_SX}
            >
              <SkipPreviousIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('audio.next')}>
            <IconButton
              onClick={onNext}
              aria-label={t('audio.next')}
              size="small"
              sx={CLUSTER_BTN_SX}
            >
              <SkipNextIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {volume !== undefined && onVolumeChange && (
            <VolumeControl
              volume={volume}
              onVolumeChange={onVolumeChange}
              triggerSx={{ display: { xs: 'none', sm: 'inline-flex' }, width: 32, height: 32 }}
            />
          )}

          {surahRepeatPlaythrough && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              aria-label={t('playlists.surahRepeatPlaythrough', {
                current: surahRepeatPlaythrough.current,
                total: surahRepeatPlaythrough.total,
              })}
              sx={{
                fontVariantNumeric: 'tabular-nums',
                fontFeatureSettings: '"tnum"',
                flexShrink: 0,
                userSelect: 'none',
                lineHeight: 1,
              }}
            >
              {`${padToWidth(
                String(surahRepeatPlaythrough.current),
                String(surahRepeatPlaythrough.total).length,
              )}/${surahRepeatPlaythrough.total}`}
            </Typography>
          )}

          {repeatMode !== undefined && onCycleRepeat && (
            <Tooltip title={repeatLabel}>
              <IconButton
                onClick={onCycleRepeat}
                size="small"
                aria-label={repeatLabel}
                sx={{
                  ...CLUSTER_BTN_SX,
                  ...(repeatMode === 'off'
                    ? {}
                    : {
                        color: 'primary.main',
                        bgcolor: alpha(
                          theme.palette.primary.main,
                          theme.palette.mode === 'dark' ? 0.2 : 0.12,
                        ),
                      }),
                }}
              >
                {repeatIcon}
              </IconButton>
            </Tooltip>
          )}

          {continuousMode !== undefined && onToggleContinuousMode && (
            <Tooltip title={continuousLabel}>
              <IconButton
                onClick={onToggleContinuousMode}
                size="small"
                aria-label={continuousLabel}
                sx={{
                  ...CLUSTER_BTN_SX,
                  ...(continuousMode
                    ? {
                        color: 'primary.main',
                        bgcolor: alpha(
                          theme.palette.primary.main,
                          theme.palette.mode === 'dark' ? 0.2 : 0.12,
                        ),
                      }
                    : {}),
                }}
              >
                <AllInclusiveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Box>
  )
}

